import { AlertTriangle, ChevronDown, Copy, Download, Laptop, Mail, PackageX, Puzzle, RefreshCw, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, downloadAgent } from '../lib/api';
import { relative } from '../lib/format';
import { groupDevices, type Child, type Device, type UpdateStatus } from '../lib/types';
import { Sheet } from '../ui/Sheet';
import { useParent } from './ParentContext';
import { Sharing } from './StatusBits';

export function DevicesPanel({ child }: { child: Child }) {
  return (
    <div className="stack">
      <PairCard child={child} />
      <section className="card">
        <h2>Paired devices</h2>
        {!child.devices.length && <p className="muted">None yet.</p>}
        <ul className="device-list">
          {groupDevices(child.devices).map(({ device, extensions }) => (
            <DeviceRow key={device.id} device={device} extensions={extensions} />
          ))}
        </ul>
      </section>
    </div>
  );
}

function PairCard({ child }: { child: Child }) {
  const { toast, parent } = useParent();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState<{ code: string; expiresAt: number } | null>(null);
  const [agentVersion, setAgentVersion] = useState<string | null>(null);
  const [macVersion, setMacVersion] = useState<string | null>(null);
  // Which computer the steps are for: default to the parent's own kind of computer.
  const [platform, setPlatform] = useState<'windows' | 'mac'>(/Mac/i.test(navigator.platform || navigator.userAgent) ? 'mac' : 'windows');
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [, tick] = useState(0);
  const agentUrl = `${location.origin}/agent`;

  useEffect(() => {
    api
      .get<{ version: string | null; macVersion?: string | null }>('/api/agent/version')
      .then((r) => {
        setAgentVersion(r.version);
        setMacVersion(r.macVersion ?? null);
      })
      .catch(() => setAgentVersion(null));
  }, []);

  useEffect(() => {
    if (!code) return;
    const t = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [code]);

  async function newCode() {
    try {
      setCode(await api.post(`/api/children/${child.id}/pairing-code`));
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  // Email the parent the steps (or, when email isn't set up, copy them) so they can open them on
  // the child's device — handy when you add a child from your own computer. Also shows the code here.
  async function sendSetup() {
    setSending(true);
    try {
      const r = await api.post<{ emailed: boolean; code: string; expiresAt: number; instructions: string }>(
        `/api/children/${child.id}/setup-email`,
      );
      setCode({ code: r.code, expiresAt: r.expiresAt });
      if (r.emailed) toast({ text: `Setup instructions emailed to ${parent.email}.`, tone: 'good' });
      else {
        await navigator.clipboard?.writeText(r.instructions);
        toast({ text: 'Email isn’t set up, so the setup instructions were copied to your clipboard.', tone: 'good' });
      }
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setSending(false);
    }
  }

  // The installer for the chosen computer, when this server has one.
  const version = platform === 'mac' ? macVersion : agentVersion;

  async function download() {
    if (!version) return;
    setDownloading(true);
    try {
      await downloadAgent(version, platform);
      toast({ text: platform === 'mac' ? 'Downloading the Mac app…' : 'Downloading the Windows app…', tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setDownloading(false);
    }
  }

  const left = code ? Math.max(0, Math.round((code.expiresAt - Date.now()) / 1000)) : 0;

  return (
    <section className="card pair">
      <div className="row-between">
        <h2>Pair {child.name}’s computer</h2>
        <button className="btn small" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          <Laptop size={15} /> {open ? 'Hide' : 'Pair a new computer'}
        </button>
      </div>
      {open && (
        <>
      {(agentVersion || macVersion) && (
        <div className="segmented" role="radiogroup" aria-label="Child’s computer">
          {(['windows', 'mac'] as const).map((p) => (
            <button key={p} role="radio" aria-checked={platform === p} className={platform === p ? 'active' : ''} onClick={() => setPlatform(p)}>
              {p === 'mac' ? 'Mac' : 'Windows PC'}
            </button>
          ))}
        </div>
      )}
      {version && (
        <div className="pair-download">
          <button className="btn primary big" onClick={download} disabled={downloading}>
            <Download size={18} />{' '}
            {downloading ? 'Downloading…' : platform === 'mac' ? `Download the Mac app (v${version})` : `Download the Windows app (v${version})`}
          </button>
          <p className="muted small">
            {platform === 'mac'
              ? `Open it on ${child.name}’s Mac (macOS 13 or newer). ClassGuardian appears in the menu bar, asks for this address and the code below, then walks you through allowing screen recording, camera and microphone.`
              : `Run it on ${child.name}’s Windows PC as an administrator, then enter the code below when it asks.`}{' '}
            The app runs at startup and shows a School Resources button, with what ClassGuardian is doing, whenever it’s monitoring.
          </p>
        </div>
      )}
      <ol className="steps">
        {version && platform === 'mac' ? (
          <>
            <li>
              Download the Mac app above and open it on {child.name}’s Mac. If macOS says it can’t check the app, open System
              Settings ▸ Privacy &amp; Security and click <strong>Open Anyway</strong> (the app isn’t notarized by Apple yet).
            </li>
            <li>
              Click the ClassGuardian shield in the menu bar and enter this address{' '}
              <button
                className="link-btn mono"
                onClick={() => navigator.clipboard?.writeText(location.origin).then(() => toast({ text: 'Address copied.', tone: 'good' }))}
              >
                {location.origin} <Copy size={13} />
              </button>{' '}
              and the code below.
            </li>
            <li>
              Allow screen recording, camera and microphone when the setup checklist asks, so {child.name} never sees a prompt during
              school time. Sit with {child.name} so you can explain what ClassGuardian shows you.
            </li>
          </>
        ) : version ? (
          <>
            <li>Download the Windows app above and run it on {child.name}’s computer (choose “Run as administrator”).</li>
            <li>
              Setup asks for this address{' '}
              <button
                className="link-btn mono"
                onClick={() => navigator.clipboard?.writeText(location.origin).then(() => toast({ text: 'Address copied.', tone: 'good' }))}
              >
                {location.origin} <Copy size={13} />
              </button>{' '}
              and the code below. Sit with {child.name} so you can explain what ClassGuardian shows you.
            </li>
            <li>
              On a Chromebook{macVersion ? '' : ' or Mac'} instead, open{' '}
              <button className="link-btn mono" onClick={() => navigator.clipboard?.writeText(agentUrl).then(() => toast({ text: 'Link copied.', tone: 'good' }))}>
                {agentUrl} <Copy size={13} />
              </button>{' '}
              in Chrome or Edge and enter the code there.
            </li>
          </>
        ) : (
          <>
            <li>
              On {child.name}’s computer, open{' '}
              <button className="link-btn mono" onClick={() => navigator.clipboard?.writeText(agentUrl).then(() => toast({ text: 'Link copied.', tone: 'good' }))}>
                {agentUrl} <Copy size={13} />
              </button>{' '}
              in Chrome or Edge. Works on Windows, Mac and Chromebooks.
            </li>
            <li>Enter the code below. Sit with {child.name} so you can explain what ClassGuardian shows you.</li>
          </>
        )}
        <li>
          For website blocking and the website log, also add the ClassGuardian extension to that browser and pair it with a Family code
          (see <em>extension/README.md</em>).
        </li>
      </ol>
      {code && left > 0 ? (
        <div className="code-box">
          <div className="code" aria-label={`Family code ${code.code.split('').join(' ')}`}>
            {code.code.slice(0, 3)} {code.code.slice(3)}
          </div>
          <div className="muted small">
            Works for {Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')} more
          </div>
        </div>
      ) : (
        <button className="btn primary big" onClick={newCode}>
          Get a Family code
        </button>
      )}
      <div className="pair-setup-email">
        <button className="btn small" onClick={sendSetup} disabled={sending}>
          <Mail size={15} />{' '}
          {sending
            ? parent.emailAvailable
              ? 'Sending…'
              : 'Preparing…'
            : parent.emailAvailable
              ? 'Email me the setup instructions'
              : 'Copy the setup instructions'}
        </button>
        <p className="muted small">
          {parent.emailAvailable
            ? `Adding ${child.name} from your own computer? Send the steps and code to your email to open on their device.`
            : `Adding ${child.name} from your own computer? Copy the steps and code to paste onto their device.`}
        </p>
      </div>
        </>
      )}
    </section>
  );
}

/** Compare dotted versions; >0 when a is newer than b. */
function cmpVersion(a: string, b: string): number {
  const pa = a.split('.').map((n) => Number(n) || 0);
  const pb = b.split('.').map((n) => Number(n) || 0);
  for (let i = 0; i < 3; i++) if ((pa[i] ?? 0) !== (pb[i] ?? 0)) return (pa[i] ?? 0) - (pb[i] ?? 0);
  return 0;
}

const UPDATE_LABEL: Record<UpdateStatus['phase'], string> = {
  checking: 'Checking for updates…',
  downloading: 'Downloading update',
  installing: 'Installing update…',
  uptodate: 'Already up to date',
  error: 'Update failed',
};

/**
 * One paired device. A computer lists the browser extensions its app set up under it (#675), collapsible; those follow
 * the computer to whichever child is using it, so on a shared computer they move with it.
 */
function DeviceRow({ device, extensions = [], under }: { device: Device; extensions?: Device[]; under?: Device }) {
  const { refresh, toast, socket, agentLatest, agentMacLatest, updates } = useParent();
  const update = updates[device.id];
  const isAgent = device.kind === 'agent';
  const isMac = /mac/i.test(device.platform ?? '');
  // A Mac compares against the Mac app's version (#293), a PC against the Windows app's.
  // The extension updates from the browser's store (#640), so only the app has a version to update to.
  const latest = isAgent ? (isMac ? agentMacLatest : agentLatest) : null;
  // The app and the extension both report a version now.
  const reportsVersion = !!device.appVersion && (isAgent || device.kind === 'extension');
  const updateAvailable = reportsVersion && !!latest && cmpVersion(latest, device.appVersion!) > 0;
  const busy = !!update && (update.phase === 'checking' || update.phase === 'downloading' || update.phase === 'installing');
  // Only offer "Update now" when there's something to install (#252), and keep it while an update is running. Macs
  // update themselves too now (#293).
  const showUpdateNow = isAgent && reportsVersion && (updateAvailable || busy);
  const [removing, setRemoving] = useState(false);
  const pendingRemoval = !!device.uninstallRequestedAt;

  async function cancelRemoval() {
    try {
      await api.del(`/api/devices/${device.id}/uninstall`);
      await refresh();
      toast({ text: `${device.name} keeps ClassGuardian.`, tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  // A computer the family's children share (#442): each child picks themselves on it, no password.
  async function setShared(shared: boolean) {
    try {
      await api.patch(`/api/devices/${device.id}`, { shared });
      await refresh();
      toast({ text: shared ? `${device.name} is shared. Each child picks their name on it.` : `${device.name} is no longer shared.`, tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  async function unpair() {
    if (!confirm(`Unpair ${device.name}? It will need a new code to pair again.`)) return;
    try {
      await api.del(`/api/devices/${device.id}`);
      await refresh();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  function updateNow() {
    if (!socket?.send({ type: 'update-now', deviceId: device.id })) {
      toast({ text: 'Not connected. Try again in a moment.', tone: 'alert' });
      return;
    }
    toast({ text: `Asked ${device.name} to update now. It may take a few minutes.`, tone: 'info' });
  }
  return (
    <li>
      <span className="device-icon">{isAgent ? <Laptop size={20} /> : <Puzzle size={20} />}</span>
      <div className="grow">
        <div>{device.name}</div>
        <div className="muted small">
          {isAgent ? 'ClassGuardian app' : under ? 'Browser extension' : 'Browser extension, paired by hand'}
          {device.appVersion && ` · v${device.appVersion}`}
          {device.platform && !under && ` · ${device.platform}`} · {device.online ? 'online now' : device.lastSeenAt ? `seen ${relative(device.lastSeenAt)}` : 'not seen yet'}
        </div>
        {device.kind === 'extension' && (
          <div className="muted small">
            {under ? `Goes with ${under.name} to whoever is using it. ` : ''}Updates come from the browser’s extension store automatically.
          </div>
        )}
        {isAgent && (
          <label className="check small device-shared">
            <input type="checkbox" checked={!!device.shared} onChange={(e) => setShared(e.target.checked)} />
            <span>
              Shared with the family
              <span className="muted small block">
                {device.shared
                  ? 'Each child picks their name on this computer (no password) and it follows their schedule and rules.'
                  : 'Turn on if your children take turns on this computer.'}
              </span>
            </span>
          </label>
        )}
      </div>
      <Sharing device={device} />
      {showUpdateNow && (
        <button
          className="btn small"
          onClick={updateNow}
          disabled={!device.online || busy}
          title={device.online ? `Install v${latest} now` : `${device.name} needs to be online to update`}
        >
          <RefreshCw size={15} /> {busy ? 'Updating…' : 'Update now'}
        </button>
      )}
      {isAgent && !pendingRemoval && (
        <button className="btn small danger-text" onClick={() => setRemoving(true)} title={`Remove ClassGuardian from ${device.name}`}>
          <PackageX size={15} /> Uninstall…
        </button>
      )}
      <button className="icon-btn" onClick={unpair} aria-label={`Unpair ${device.name}`}>
        <Trash2 size={18} />
      </button>
      {pendingRemoval && (
        <div className="update-note uninstall-note">
          <AlertTriangle size={14} aria-hidden /> Removing ClassGuardian{device.online ? '…' : ' when this computer is next online.'}{' '}
          <button className="link-btn small" onClick={cancelRemoval}>
            Cancel
          </button>
        </div>
      )}
      {removing && <UninstallSheet device={device} onClose={() => setRemoving(false)} onDone={refresh} />}
      {extensions.length > 0 && (
        <details className="device-exts" open>
          <summary>
            <ChevronDown size={15} aria-hidden /> Browser extensions on {device.name} ({extensions.length})
          </summary>
          <ul className="device-list">
            {extensions.map((e) => (
              <DeviceRow key={e.id} device={e} under={device} />
            ))}
          </ul>
        </details>
      )}
      {updateAvailable && !update && (
        <div className="update-note">
          Update available → v{latest}
        </div>
      )}
      {update && (
        <div className={`update-progress ${update.phase}`}>
          <div className="update-bar" aria-hidden={update.phase !== 'downloading'}>
            <span style={{ width: `${update.phase === 'downloading' ? update.percent : busy ? 100 : 0}%` }} />
          </div>
          <div className="small">
            {update.phase === 'error' ? update.message || UPDATE_LABEL.error : UPDATE_LABEL[update.phase]}
            {update.phase === 'downloading' && ` ${update.percent}%`}
            {update.version && (update.phase === 'downloading' || update.phase === 'installing') && ` → v${update.version}`}
          </div>
        </div>
      )}
    </li>
  );
}

/**
 * Remove ClassGuardian from a child's computer (#572). A warning first: it can't be undone, and the computer needs the
 * app installed and paired again to be supervised.
 */
function UninstallSheet({ device, onClose, onDone }: { device: Device; onClose: () => void; onDone: () => Promise<void> | void }) {
  const { toast } = useParent();
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  async function remove() {
    setBusy(true);
    try {
      const r = await api.post<{ delivered: boolean }>(`/api/devices/${device.id}/uninstall`);
      await onDone();
      toast({
        text: r.delivered ? `Removing ClassGuardian from ${device.name}. It takes a minute or two.` : `${device.name} is offline. ClassGuardian is removed when it’s next online.`,
        tone: 'good',
      });
      onClose();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
      setBusy(false);
    }
  }
  return (
    <Sheet title={`Uninstall from ${device.name}?`} onClose={onClose}>
      <div className="stack uninstall-sheet">
        <p className="uninstall-warn">
          <AlertTriangle size={20} aria-hidden />
          <span>
            <strong>This can’t be undone.</strong> ClassGuardian is removed from {device.name} and unpaired. To supervise this computer again, you’ll need
            to install the app on it and pair it again.
          </span>
        </p>
        <ul className="muted small">
          <li>Website and app limits, school time, live view and School Resources stop on this computer.</li>
          <li>Your records (planner, grades, attendance, reading) are kept.</li>
          <li>{device.online ? 'It’s online, so it happens in a minute or two.' : 'It’s offline now, so it happens when it’s next online. You can cancel until then.'}</li>
        </ul>
        <label className="check">
          <input type="checkbox" checked={sure} onChange={(e) => setSure(e.target.checked)} />
          <span>I understand this can’t be undone and the app will need to be reinstalled.</span>
        </label>
        <div className="row-gap wrap">
          <button className="btn danger-solid" onClick={remove} disabled={!sure || busy}>
            <PackageX size={16} /> {busy ? 'Removing…' : 'Uninstall ClassGuardian'}
          </button>
          <button className="btn" onClick={onClose}>
            Keep it
          </button>
        </div>
      </div>
    </Sheet>
  );
}

